/**
 * Le plan du soir, coupé en deux : le jour visé, puis les jours d'après.
 *
 * Demandé par Hedi le 6 septembre 2026 : *« donne plus de priorités aux
 * exercices correspondant aux séances de demain mais inclus s'il y a du temps
 * ceux des jours d'après »*. Le serveur range la liste dans cet ordre et marque
 * chaque compétence du jour de cours dont elle vient ; les écrans ne
 * réordonnent rien, ils **coupent**.
 *
 * **Ici et non dans une des deux coquilles.** L'enfant lit `CompetenceARevoir`,
 * le parent lit `CompetencePrevue` : deux schémas, une seule règle, et la
 * coquille enfant n'a pas le droit d'importer `parent/` (règle tenue par un test
 * de `child/carte.test.ts`). Recopiée des deux côtés, cette coupe finirait par
 * diverger, et le parent lirait alors un plan qui n'est pas celui de l'enfant.
 */

/** Ce que la coupe a besoin de savoir d'une compétence : son jour, s'il existe. */
export type AvecJour = { pour?: string | null };

/**
 * `dabord` est ce qui vient du jour visé, `ensuite` ce qui vient d'ailleurs.
 *
 * **La coupe se fait sur une égalité de dates, jamais sur un rang.** Une liste
 * où les premières lignes seraient « pour demain » par convention afficherait le
 * mauvais titre le soir où une matière de demain manque de stock.
 *
 * **Égalité, et non « avant / après ».** Sous le cap « mon prochain contrôle »,
 * le jour visé est celui de l'épreuve — jusqu'à quinze jours devant — et les
 * places libres sont remplies par la semaine qui vient, donc par des jours
 * *antérieurs*. Une comparaison ordonnée les rangerait dans la préparation du
 * contrôle, ce qu'ils ne sont pas.
 *
 * Sans jour visé — choix libre d'une matière, révision d'été —, ou sans `pour`,
 * tout reste dans `dabord` : il n'y a alors aucun « plus tard » à annoncer, et
 * inventer une section sur une donnée absente serait pire que ne rien dire.
 */
export function separerParJour<T extends AvecJour>(
  competences: T[],
  le: string | null | undefined,
): { dabord: T[]; ensuite: T[] } {
  if (!le) return { dabord: competences, ensuite: [] };
  return {
    dabord: competences.filter((c) => !c.pour || c.pour === le),
    ensuite: competences.filter((c) => !!c.pour && c.pour !== le),
  };
}

/**
 * Ce que les jours d'après ont laissé de côté faute de preuve, en une phrase.
 *
 * Hedi, 8 septembre 2026 : *« Pourquoi je vois encore des exercices de SVT alors
 * que Baya me dit qu'elle n'a encore fait aucun chapitre de SVT ? »* Elle avait
 * raison — aucune entrée ne nommait la SVT, les compétences n'étaient ouvertes
 * que par le découpage théorique du cycle — et le plan lui en servait trois pour
 * une séance du 15 septembre. Le débordement se borne désormais à ce dont une
 * source a parlé (`travail_du_jour._ensuite`), et **le raccourcissement se dit** :
 * une liste plus courte sans raison visible se lit comme une réserve vide, ce
 * qui appelle le geste inverse du bon.
 *
 * `avecNombres` est faux chez l'enfant — invariant §14.1, un compte de chapitres
 * écartés est un bulletin —, vrai chez le parent, qui est le seul des deux à
 * pouvoir faire quelque chose du nombre : c'est le même arbitrage que
 * `phraseDuManque`, et pour la même raison.
 */
export function phraseSansPreuve(combien: number, avecNombres = false): string {
  if (combien <= 0) return "";
  if (!avecNombres) {
    return (
      "D'autres chapitres des jours qui viennent ne sont pas proposés : rien ne " +
      "dit encore qu'ils ont été traités en classe. Ils reviendront dès qu'un " +
      "cours, un devoir ou ton journal en parlera."
    );
  }
  const pluriel = combien > 1 ? "s" : "";
  return (
    `${combien} chapitre${pluriel} des journées suivantes ${combien > 1 ? "ont" : "a"} ` +
    "été écarté" +
    pluriel +
    " : seul le découpage théorique de l'année les ouvre, aucune entrée " +
    "(cahier de textes, contenu de séance, journal du soir) n'atteste qu'ils ont " +
    "été traités. Vous pouvez les déclarer vus depuis l'onglet « Ce qui a été vu »."
  );
}
